import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../../services/axios';
import { showSuccessToast, showErrorToast } from '../../components/ui/Toast';
import Table from '../../components/tableUtilities/Table';
import Header from '../../components/utility/Header';
import StyledCard from '../../components/Cards/StyledCard';
import Loader from '../../components/Loaders/Loader';
import IconWrapper from '../../components/Cards/IconWrapper';
import { CircleX } from 'lucide-react';
import Container from '../../components/Cards/Container';
import { useAuthContext } from '../../context/AuthProvider';
import { getRoute, ROUTE_KEY } from '../../config/permissions.config';
import LoaderModal from '../../components/Loaders/LoaderModal';
import { getShortlistedCandidates } from '../../services/admin.candidate.service';
import useDebounce from '../../hooks/useDebounce';
import * as Sentry from '@sentry/react';

const Shortlisted = () => {
    const { user , isLoading } = useAuthContext();
    const [location,setLocation] = useState(null);
    const [filters,setFilters] = useState({});
    const [page,setPage] = useState(1);
    const [pageSize,setPageSize] = useState(10);
    const [filterObj,setFilterObj] = useState({});
    const [sortFilterObj,setSortFilterObj] = useState({});
    const [sortModel,setSortModel] = useState([])
    const [search,setSearch] = useState("");
    const [showContractors, setShowContractors] = useState(false);

    const [debouncedQuery] = useDebounce(search,400);

    const [isFiltered, setIsFiltered] = useState(false);

    useEffect(() => {
    const cleanedFilters = Object.fromEntries(
        Object.entries(filters).filter(([key, value]) =>
        Array.isArray(value) ? value.length > 0 : !!value
        )
    );

    setFilterObj({ ...cleanedFilters, showContractors });

    // Check if any filter field is populated
    const hasFiltersApplied = Object.values(cleanedFilters).some(value =>
        Array.isArray(value) ? value.length > 0 : !!value
    );

    setIsFiltered(hasFiltersApplied || showContractors);
    }, [filters, showContractors]);

    useEffect(()=>{
        setIsFiltered(prev => prev ? prev : debouncedQuery)
    },[debouncedQuery])

    //Server-side sort management for tables
    useEffect(() => {
        const selectedSort = {}
        sortModel.map(model => {
            selectedSort[model.field] = model.sort
        })
        setSortFilterObj(selectedSort);
    }, [sortModel]);
    
    const queryClient = useQueryClient();

    // Fetch shortlisted candidates
    const { data, isLoading : isCandidatesLoading , isError, error } = useQuery({
        queryKey: ['shortlistedCandidates',location,,page,pageSize,filterObj,debouncedQuery,sortFilterObj],
        queryFn: () => getShortlistedCandidates({companyId : user?.companyDetails?._id,...(location ? location : {}),page : page + 1 ,pageLimit : pageSize ,filter : filterObj ,search : debouncedQuery, sortFilters : sortFilterObj}),
        enabled : !!user?.companyDetails
    });

    const shortlistMutation = useMutation({
        mutationFn: ({ candidateId, jobId, shortlisted }) =>
            axios.post(`/admin/candidate/${candidateId}/job/${jobId}/shortlist`, { shortlisted }),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries(['shortlistedCandidates']);

            // Show different toast messages based on action performed
            if (variables.shortlisted) {
                showSuccessToast("Added to Future Gems", "Candidate has been added to Future Gems");
            } else {
                showErrorToast("Removed", "Candidate has been removed from Future Gems");
            }
        },
        onError: (error) => {
            console.error("Error updating shortlist status:", error);
            showErrorToast("Error", "Failed to update Future Gems status");
        }
    });

    // Format data for the table
    const formatCandidatesForTable = (candidates) => {
        if (!candidates) return [];

        return candidates.flatMap(candidate =>
            candidate.applications.map(application => ({
                _id: candidate._id,
                jobId: application.jobId,
                firstName: candidate.firstName,
                lastName: candidate.lastName,
                email: candidate.email,
                phone: candidate.phone,
                profilePictureUrl: candidate.profilePictureUrl,
                location: candidate.location,
                portfolio: candidate.portfolio,
                website: candidate.website,
                resumeUrl: candidate.resumeUrl,
                experience: candidate.experience,
                // Professional info
                currentCTC: application.currentCTC,
                expectedCTC: application.expectedCTC,
                hourlyRate: application.hourlyRate,
                // Stage info
                currentStage: application.currentStage,
                status: application.status,
                // Rating
                rating: application.rating,
                // Job details
                jobApplied: application.jobApplied,
                jobTitle: application.jobApplied, // Add jobTitle mapped from jobApplied for the table
                // Contractor filter support
                jobType: application.jobType || (application.hourlyRate > 0 ? "Contract" : "Full Time"),
                // Add stageStatuses for components that need them
                stageStatuses: application.stageStatuses || {},
                // Add any other needed fields
                hasGivenAssessment: candidate.hasGivenAssessment || false
            }))
        );
    };

    // Custom shortlist column
    const getShortlistColumn = () => [
        {
            field: 'shortlistAction',
            headerName: 'Action',
            width: 120,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            disableColumnMenu: true,
            renderCell: (params) => (
                <div
                    className="flex cursor-pointer h-full items-center justify-center"
                    onClick={(e) => {
                        e.stopPropagation();
                        shortlistMutation.mutate({
                            candidateId: params.row._id,
                            jobId: params.row.jobId,
                            shortlisted: false // Remove from shortlist
                        });
                    }}
                >
                    <IconWrapper icon={CircleX} isErrorIcon size={0} customIconSize={5} />
                </div>
            ),
        }
    ];

    if (isError) return <div>Error: {error.message}</div>;

    const tableData = formatCandidatesForTable(data?.candidates);

    const getShortlistedCandidatesExportData = async () => {
        try {
            const response = await getShortlistedCandidates({companyId : user?.companyDetails?._id,...(location ? location : {}),filter : filterObj ,search : debouncedQuery, sortFilters : sortFilterObj});
            return formatCandidatesForTable(response?.candidates)
        } catch (error) {
            Sentry.captureException(error, {
              tags: { file: "Shortlisted.jsx", action: "getShortlistedCandidatesExportData", role: "admin" },
              extra: { response: error?.response?.data, message: error?.message },
            });
            console.log("Export data error :",error)
        }
    }

    // Create dummy jobData to help with budget filtering for contractors
    const jobData = {
        employmentType: "Mixed" // This will allow both contract and full-time filtering
    };

    return (
        <Container>
            {(isLoading || isCandidatesLoading) && <LoaderModal />}
            <Header HeaderText={"Future Gems"} />
            <StyledCard padding={2} >
                {((data?.totalCount || 0) === 0 && !isFiltered) ? 
                (
                    <div className="text-center py-8 bg-background-100 rounded-xl p-6">
                        <h2 className="text-font-gray cursor-default">No shortlisted candidates found.</h2>
                        <p className="typography-large-p mt-2 cursor-default">
                            Start shortlisting candidates to see them here.
                        </p>
                    </div>
                )
                : (
                    <Table
                        hasCheckBox={false}
                        readOnly={true}
                        isShortlisted
                        addLocationFilter={setLocation}
                        currentPage={page} 
                        setCurrentPage={setPage}
                        pageSize={pageSize} 
                        setPageSize={setPageSize}
                        filters={filters}
                        setFilters={setFilters}
                        sortModel={sortModel}
                        setSortModel={setSortModel}
                        searchTerm={search}
                        setSearchTerm={setSearch}
                        showContractors={showContractors}
                        setShowContractors={setShowContractors}
                        readOnlyData={tableData}
                        getDataWithoutPagination={getShortlistedCandidatesExportData}
                        additionalColumns={getShortlistColumn()}
                        totalCount={data?.totalCount}
                        jobData={jobData} // Pass job data for employment type filtering
                        customNavigationPath={getRoute(user?.role,ROUTE_KEY.SHORTLISTED_VIEW_CANDIDATE)}// Custom navigation path for shortlisted view
                    />
                )}
            </StyledCard>
        </Container>
    );
};

export default Shortlisted;